// API Client - All calls go to Cloudflare Worker
const API_BASE = import.meta.env.PROD 
  ? 'https://izwi-api.orbxcode.workers.dev/api' 
  : '/api';

// Generic fetch helper
async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(err.error || 'Request failed');
  }
  return res.json();
}

// Auth
export const auth = {
  signUp: (data: { name: string; email: string; password: string }) =>
    apiFetch('/auth/sign-up', { method: 'POST', body: JSON.stringify(data) }),
  signIn: (data: { email: string; password: string }) =>
    apiFetch('/auth/sign-in', { method: 'POST', body: JSON.stringify(data) }),
  signOut: () => apiFetch('/auth/sign-out', { method: 'POST' }),
  getSession: () => apiFetch('/auth/get-session'),
};

// Wallet
export const wallet = {
  get: (userId: string) => apiFetch(`/wallet/${userId}`),
  getStats: (userId: string) => apiFetch(`/wallet/${userId}/stats`),
};

// Annotations
export const annotations = {
  submit: (data: { frameId: string; transcription: string; userId?: string }) =>
    apiFetch('/annotations', { method: 'POST', body: JSON.stringify(data) }),
  list: (userId: string) => apiFetch(`/annotations?user_id=${userId}`),
};

// Jobs
export const jobs = {
  create: (data: { filename: string; targetLanguage: string; targetDialect?: string; userId?: string }) =>
    apiFetch('/transcribe/jobs', { method: 'POST', body: JSON.stringify(data) }),
  list: (userId?: string) => apiFetch(`/transcribe/jobs${userId ? `?user_id=${userId}` : ''}`),
  get: (id: string) => apiFetch(`/transcribe/jobs/${id}`),
};

// Transcribe
export const transcribe = {
  process: (data: { audio_base64: string; language_id: string; dialect_id?: string; tier?: string }) =>
    apiFetch('/transcribe', { method: 'POST', body: JSON.stringify(data) }),
};

// Languages
export const languages = {
  list: (params?: { country?: string; search?: string }) => {
    const query = new URLSearchParams(params as any).toString();
    return apiFetch(`/languages${query ? `?${query}` : ''}`);
  },
  countries: () => apiFetch('/languages/countries'),
};

// Vault
export const vault = {
  list: (language?: string) => apiFetch(`/vault${language ? `?language=${language}` : ''}`),
  get: (id: string) => apiFetch(`/vault/${id}`),
};

// FastTag (annotation UI)
export const fasttag = {
  getBatch: (annotatorId: string, batchSize?: number) =>
    apiFetch(`/fasttag/batch?annotator_id=${annotatorId}${batchSize ? `&batch_size=${batchSize}` : ''}`),
  submit: (data: { segment_id: string; annotator_id: string; corrections: any[]; session_time_ms: number }) =>
    apiFetch('/fasttag/submit', { method: 'POST', body: JSON.stringify(data) }),
  verify: (data: { segment_id: string; word_index: number; annotator_id: string }) =>
    apiFetch('/fasttag/verify', { method: 'POST', body: JSON.stringify(data) }),
  yield: (days?: number) => apiFetch(`/fasttag/yield${days ? `?days=${days}` : ''}`),
};

// Metering
export const metering = {
  estimate: (data: { audio_duration_seconds: number; tier: string }) =>
    apiFetch('/metering/estimate', { method: 'POST', body: JSON.stringify(data) }),
  profitDashboard: (days?: number) => apiFetch(`/metering/dashboard/profit${days ? `?days=${days}` : ''}`),
};

// ALKE (morphology, cognates)
export const alke = {
  analyzeMorphology: (data: { word: string; language?: string }) =>
    apiFetch('/alke/morphology/analyze', { method: 'POST', body: JSON.stringify(data) }),
  tokenize: (data: { text: string; language?: string }) =>
    apiFetch('/alke/morphology/tokenize', { method: 'POST', body: JSON.stringify(data) }),
  searchWords: (query: string, languages?: string[]) =>
    apiFetch(`/alke/words?q=${query}${languages ? `&languages=${languages.join(',')}` : ''}`),
  getCognates: (wordId: string) => apiFetch(`/alke/words/${wordId}/cognates`),
  getCousinSuggestions: (word: string, languageId: string) =>
    apiFetch(`/alke/cousin-suggestions?word=${word}&language_id=${languageId}`),
  trainingDashboard: () => apiFetch('/alke/training/dashboard'),
  detectDialect: (data: { text: string }) =>
    apiFetch('/alke/dialect/detect', { method: 'POST', body: JSON.stringify(data) }),
};

// Admin
export const admin = {
  stats: () => apiFetch('/admin/stats'),
  users: () => apiFetch('/admin/users'),
  payouts: (status?: string) => apiFetch(`/admin/payouts${status ? `?status=${status}` : ''}`),
  updatePayout: (id: string, data: { status: string; reference?: string }) =>
    apiFetch(`/admin/payouts/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  config: () => apiFetch('/admin/config'),
  setConfig: (key: string, value: string) =>
    apiFetch('/admin/config', { method: 'POST', body: JSON.stringify({ key, value }) }),
};

// Products (for marketplace)
export interface Product {
  id: string;
  name: string;
  description: string;
  category: string;
  price_cents: number;
  features: string[];
}

export const products = {
  list: (): Promise<Product[]> => Promise.resolve([
    { id: 'morph-api', name: 'Morpheme Splitter API', description: 'Split Bantu words into morphemes', category: 'api', price_cents: 100, features: ['REST API', 'Batch processing'] },
    { id: 'cognate-api', name: 'Cognate Finder API', description: 'Find related words across languages', category: 'api', price_cents: 150, features: ['Cross-language search', 'Similarity scores'] },
    { id: 'rlhf-dataset', name: 'RLHF Training Set', description: 'Human-verified corrections for fine-tuning', category: 'dataset', price_cents: 10000, features: ['10k+ pairs', 'Multiple languages'] },
    { id: 'bulk-transcripts', name: 'Bulk Transcripts', description: 'Verified audio transcriptions', category: 'dataset', price_cents: 5000, features: ['100+ hours', 'IPA included'] },
  ]),
};

// Gemini service (for draft analysis) - now uses real AI transcription
export const gemini = {
  draftAnalysis: async (imageBase64: string, audioBase64: string, language: string, dialect?: string) => {
    try {
      // If we have audio, transcribe it
      if (audioBase64 && audioBase64 !== 'audio_placeholder') {
        const transcription = await apiFetch<{ text: string; confidence: number }>('/ai/transcribe', {
          method: 'POST',
          body: JSON.stringify({ audio_base64: audioBase64, language })
        });
        
        return {
          visualDescription: 'Video frame analyzed',
          transcription: transcription.text,
          audioConfidence: Math.round(transcription.confidence * 100),
          visualConfidence: 85,
          linguisticFlags: [],
          isCodeSwitched: false,
          detectedLanguages: [language],
        };
      }
      
      // Fallback: Use LLM to analyze the image context
      const analysis = await apiFetch<{ text: string }>('/ai/chat', {
        method: 'POST',
        body: JSON.stringify({
          messages: [
            { role: 'system', content: `You are analyzing video frames for ${language} language transcription. Describe what you see briefly.` },
            { role: 'user', content: 'Describe this video frame context for transcription purposes.' }
          ],
          maxTokens: 100
        })
      });
      
      return {
        visualDescription: analysis.text || 'Video frame showing speaker',
        transcription: '',
        audioConfidence: 0,
        visualConfidence: 75,
        linguisticFlags: [],
        isCodeSwitched: false,
        detectedLanguages: [language],
      };
    } catch (error) {
      console.error('Draft analysis error:', error);
      // Return mock data on error
      return {
        visualDescription: 'Video frame (analysis unavailable)',
        transcription: 'Transcription pending...',
        audioConfidence: 50,
        visualConfidence: 50,
        linguisticFlags: [],
        isCodeSwitched: false,
        detectedLanguages: [language],
      };
    }
  },
};

// AI Service (Workers AI + AI Gateway)
export const ai = {
  chat: (messages: { role: 'system' | 'user' | 'assistant'; content: string }[], options?: { model?: string; maxTokens?: number; temperature?: number }) =>
    apiFetch<{ text: string; usage?: any }>('/ai/chat', { method: 'POST', body: JSON.stringify({ messages, ...options }) }),
  
  transcribe: (audioBase64: string, language?: string) =>
    apiFetch<{ text: string; confidence: number; language?: string }>('/ai/transcribe', { method: 'POST', body: JSON.stringify({ audio_base64: audioBase64, language }) }),
  
  embed: (texts: string[]) =>
    apiFetch<{ embeddings: number[][] }>('/ai/embed', { method: 'POST', body: JSON.stringify({ texts }) }),
  
  analyze: (text: string, language: string) =>
    apiFetch<{ morphemes: string[]; nounClass?: number; tense?: string; suggestions: string[] }>('/ai/analyze', { method: 'POST', body: JSON.stringify({ text, language }) }),
  
  cognates: (word: string, sourceLanguage: string, targetLanguages?: string[]) =>
    apiFetch<{ cognates: { word: string; language: string; similarity: number; meaning?: string }[] }>('/ai/cognates', { method: 'POST', body: JSON.stringify({ word, sourceLanguage, targetLanguages }) }),
  
  models: () => apiFetch<{ chat: any[]; transcription: any[]; embedding: any[] }>('/ai/models'),
};

// Media (R2 Storage)
export const media = {
  upload: (data: { data: string; filename?: string; type?: 'audio' | 'video'; job_id?: string }) =>
    apiFetch<{ id: string; key: string; url: string; size: number }>('/media/upload', { method: 'POST', body: JSON.stringify(data) }),
  
  get: (key: string) => `${API_BASE}/media/${key}`,
  
  delete: (key: string) =>
    apiFetch<{ success: boolean }>(`/media/${key}`, { method: 'DELETE' }),
  
  stats: () =>
    apiFetch<{ totalFiles: number; totalSizeBytes: number; totalSizeMB: string; audioFiles: number; videoFiles: number }>('/media/stats'),
};

// Payments (Dodo Payments)
export const payments = {
  payout: (data: { user_id: string; amount_cents: number; method?: string }) =>
    apiFetch<{ payout_id: string; dodo_id?: string; amount_cents: number; status: string; message?: string }>('/payments/payout', { method: 'POST', body: JSON.stringify(data) }),
  
  checkout: (data: { item_id: string; user_id: string }) =>
    apiFetch<{ checkout_url: string; session_id: string }>('/payments/checkout', { method: 'POST', body: JSON.stringify(data) }),
  
  getPayoutStatus: (id: string) =>
    apiFetch<{ id: string; user_id: string; amount_cents: number; status: string; reference?: string }>(`/payments/payout/${id}`),
  
  history: (userId: string) =>
    apiFetch<{ payouts: any[]; transactions: any[] }>(`/payments/history/${userId}`),
  
  status: () =>
    apiFetch<{ provider: string; configured: boolean; features: { payouts: boolean; checkout: boolean; webhooks: boolean } }>('/payments/status'),
};
